import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";

export type PinProtectedAction =
  | { kind: "OPEN" }
  | { kind: "SAVE" | "SUBMIT"; payload: ShiftProductionFormPayload };

export type PinGateState = {
  action: PinProtectedAction | null;
  pin: string;
  error: string | null;
};

export const emptyPinGateState: PinGateState = {
  action: null,
  pin: "",
  error: null,
};

export function beginPinAction(action: PinProtectedAction): PinGateState {
  return { action, pin: "", error: null };
}

export function changePin(state: PinGateState, value: string): PinGateState {
  return {
    ...state,
    pin: value.replace(/\D/gu, "").slice(0, 4),
    error: null,
  };
}

export function rejectPin(state: PinGateState, error: string): PinGateState {
  return { ...state, pin: "", error };
}

export function cancelPin(): PinGateState {
  return emptyPinGateState;
}

export function resolveShiftFormView(
  managerRequested: boolean,
  actorKind: "OPERATOR" | "APPROVER" | "CORPORATE" | null,
) {
  if (!managerRequested) return "EDITOR" as const;
  return actorKind === "CORPORATE" ? ("MANAGER" as const) : ("AUTH" as const);
}

export function isPrivateFormLocked(
  ownerOperatorId: string | null,
  authenticatedOperatorId: string | null,
) {
  return (
    ownerOperatorId !== null && ownerOperatorId !== authenticatedOperatorId
  );
}

export function canRetryCreateForOperator(
  attemptedOperatorId: string | null,
  authenticatedOperatorId: string,
) {
  return (
    attemptedOperatorId === null ||
    attemptedOperatorId === authenticatedOperatorId
  );
}

export function requiresOperatorEntry(
  verifiedOperatorId: string | null,
  sessionOperatorId: string | null,
) {
  return (
    verifiedOperatorId === null || verifiedOperatorId !== sessionOperatorId
  );
}

export function operatorAccessState(input: {
  verifiedOperatorId: string | null;
  sessionOperatorId: string | null;
  formOwnerId: string | null;
  pendingAction: boolean;
}) {
  const entryRequired = requiresOperatorEntry(
    input.verifiedOperatorId,
    input.sessionOperatorId,
  );
  const privateLocked = isPrivateFormLocked(
    input.formOwnerId ?? input.verifiedOperatorId,
    input.sessionOperatorId,
  );
  const gateOpen = input.pendingAction || entryRequired || privateLocked;
  return {
    entryRequired,
    privateLocked,
    gateOpen,
    maskPrivateForm:
      Boolean(input.verifiedOperatorId || input.formOwnerId) && gateOpen,
  };
}
